import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

// Common Components
import { Loader } from './components/common/Loader';
import { Cursor } from './components/common/Cursor';
import { Overlays } from './components/common/Overlays';
import { Navbar } from './components/layout/Navbar';
import { AuthModal } from './components/common/AuthModal';
import { UploadModal } from './components/common/UploadModal';

// Section Components
import { Hero } from './components/sections/Hero';
import { Marquee } from './components/sections/Marquee';
import { Counter } from './components/sections/Counter';
import { Timeline } from './components/sections/Timeline';
import { Quote } from './components/sections/Quote';
import { Gallery } from './components/sections/Gallery';
import { Footer } from './components/layout/Footer';

// Pages
import { AlbumPage } from './pages/AlbumPage';
import { GalleryPage } from './pages/GalleryPage';

// API
import { galleryApi } from './services/gallery';

// Styles
import './App.css';

import { NotFoundPage } from './pages/NotFoundPage';

import { AdminDashboard } from './pages/AdminDashboard';

// === ГЛАВНАЯ СТРАНИЦА ===
const HomePage = ({ onOpenAuth, onUploadClick, onSearch, onClearSearch }) => {
  const { user, logout } = useAuth();
  const galleryRef = useRef(null);
  const location = useLocation();

  const [loaded, setLoaded] = useState(false);
  const [loadingGallery, setLoadingGallery] = useState(true);
  const [galleryItems, setGalleryItems] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [searchInfo, setSearchInfo] = useState(null);
  const [searchVersion, setSearchVersion] = useState(0);
  const [viewMode, setViewMode] = useState('compact');

  useEffect(() => {
    if (location.hash === '#gallery' && galleryRef.current) {
      // Небольшая задержка, чтобы контент успел отрендериться
      const timer = setTimeout(() => {
        galleryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  useEffect(() => {
    galleryApi.getPhotos()
      .then(data => {
        setGalleryItems(data);
        setLoadingGallery(false);
        setLoaded(true);
      })
      .catch(err => {
        console.error('❌ Ошибка загрузки галереи:', err);
        setLoadingGallery(false);
        setLoaded(true); //Даже при ошибке — показываем Hero
      });
  }, []);

  const handleUploadSuccess = () => {
    galleryApi.getPhotos().then(data => {
      setGalleryItems(data);
      setSearchResults(null);
      setSearchInfo(null);
      setSearchVersion(prev => prev + 1);
      setViewMode('all');
    });
  };

  const handleSearch = async (searchParams) => {
    try {
      console.log(' App получил параметры:', searchParams);
      const results = await galleryApi.search(searchParams);

      const photos = results.filter(r => !r.is_album).length;
      const albums = results.filter(r => r.is_album).length;

      setSearchResults(results);
      setSearchInfo({ total: results.length, photos, albums });
      setSearchVersion(prev => prev + 1);

      setTimeout(() => {
        galleryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      console.error('❌ Ошибка поиска в App:', err);
    }
  };

  const handleClearSearch = () => {
    setSearchResults(null);
    setSearchInfo(null);
    setSearchVersion(prev => prev + 1);
    setViewMode('compact');
  };

  const navigate = useNavigate();

  const handleGalleryClick = (item) => {
    if (item.is_album) {
      navigate(`/album/${item.group_id}`);
    } else {
      navigate(`/photo/${item.pk_photo}`);
    }
  };

  return (
    <>
      <Navbar
        user={user}
        logout={logout}
        onOpenAuth={onOpenAuth}
        onUploadClick={onUploadClick}
        onSearch={handleSearch}
        onClearSearch={handleClearSearch}
      />

      <Hero loaded={loaded} />
      <Marquee text={`Архивные фотографии • 1934 — ${new Date().getFullYear()} • Забытые мгновения • `} />
      <Counter totalPhotos={galleryItems.length} />
      <Quote />

      <div ref={galleryRef} id="gallery">
        {loadingGallery ? (
          <div style={{ padding: '100px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
            <span className="reveal visible">Проявка архива...</span>
          </div>
        ) : (
          <>
            {searchInfo && (
              <div className="search-status-banner">
                {searchInfo.total > 0 ? (
                  <>
                    Найдено: <strong>{searchInfo.total}</strong> объектов
                  </>
                ) : (
                  <>По вашему запросу <strong>ничего не найдено</strong>. Попробуйте изменить параметры поиска.</>
                )}
              </div>
            )}

            <Gallery
              key={`gallery-${searchVersion}`}
              items={searchResults !== null ? searchResults : galleryItems}
              onOpen={handleGalleryClick}
              viewMode={viewMode}
              setViewMode={setViewMode}
            />
          </>
        )}
      </div>

      <Marquee text="Сохраняя прошлое • Для будущего • Каждое фото — история • " reverse />
      <Footer />
    </>
  );
};

// === РОУТИНГ ===
const AppRoutes = ({ onOpenAuth, onUploadClick }) => {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <HomePage
            onOpenAuth={onOpenAuth}
            onUploadClick={onUploadClick}
          />
        }
      />
      <Route path="/album/:albumId" element={<AlbumPage />} />
      <Route path="/photo/:photoId" element={<GalleryPage />} />
      <Route path="/admin" element={<AdminDashboard />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

// === ВНУТРЕННИЙ КОНТЕНТ (использует useAuth) ===
const AppContent = () => {
  const { user, logout } = useAuth();

  // Состояние модальных окон (авторизация и загрузка)
  const [authModal, setAuthModal] = useState({ open: false, mode: 'login' });
  const [uploadModal, setUploadModal] = useState(false);

  const handleUploadSuccess = () => {
    window.location.reload();
  };

  return (
    <>
      <Loader onComplete={() => { }} />
      <Cursor />
      <Overlays />

      {/* Роутинг + контент */}
      <AppRoutes
        onOpenAuth={(mode) => setAuthModal({ open: true, mode })}
        onUploadClick={() => setUploadModal(true)}
      />

      {/* Модальные окна (поверх роутинга) */}
      <AuthModal
        isOpen={authModal.open}
        mode={authModal.mode}
        onClose={() => setAuthModal({ open: false, mode: 'login' })}
      />

      <UploadModal
        isOpen={uploadModal}
        onClose={() => setUploadModal(false)}
        onUploadSuccess={handleUploadSuccess}
      />
    </>
  );
};

// === ОСНОВНОЙ APP (оборачивает всё в AuthProvider) ===
export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}